import type { Transaction } from '@atlaskit/editor-prosemirror/state';
import { PluginKey } from '@atlaskit/editor-prosemirror/state';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';

import type { EditorOfflineStoragePlugin } from '../editorOfflineStoragePluginType';
import type { StepsDatabase } from '../stepsDatabase';

import { resyncOnline } from './resync-online';

export type EditorOfflineStoragePluginStatus = 'pending' | 'restoring' | 'ready' | 'tooManyRetries';

export type EditorOfflineStoragePluginState = {
	status: EditorOfflineStoragePluginStatus;
	retryCount: number;
};

export const editorOfflineStoragePluginKey = new PluginKey<EditorOfflineStoragePluginState>(
	'editorOfflineStoragePlugin',
);

export const createPlugin =
	(api: PublicPluginAPI<EditorOfflineStoragePlugin> | undefined, stepsDatabase: StepsDatabase) =>
	() => {
		return new SafePlugin<EditorOfflineStoragePluginState>({
			key: editorOfflineStoragePluginKey,
			state: {
				init() {
					return {
						status: 'pending',
						retryCount: 0,
					};
				},
				apply: (tr: Transaction, currentPluginState: EditorOfflineStoragePluginState) => {
					const meta = tr.getMeta(editorOfflineStoragePluginKey);
					if (meta) {
						return {
							...currentPluginState,
							...meta,
						};
					}
					return currentPluginState;
				},
			},
			view: (editorView: EditorView) => {
				const unsubscribe = api?.connectivity?.sharedState.onChange(
					({ prevSharedState, nextSharedState }) => {
						const { status } = editorOfflineStoragePluginKey.getState(editorView.state) ?? {};
						if (status !== 'ready') {
							return;
						}
						if (prevSharedState.mode === 'offline' && nextSharedState.mode === 'online') {
							// Try to prioritise the "active" tab by delaying the inactive tabs, this is to minimise
							// our chance of disrupting the user by the reset
							if (document && !document?.hasFocus?.()) {
								// Give the active document ~1s to connect
								setTimeout(
									() => resyncOnline(api, stepsDatabase, editorView),
									document?.visibilityState === 'visible' ? 500 : 1000,
								);
							} else {
								void resyncOnline(api, stepsDatabase, editorView);
							}
						} else if (nextSharedState.mode === 'offline') {
							void stepsDatabase.clear(true).then((cleared) => {
								if (cleared) {
									stepsDatabase.releaseSyncLock?.();
								}
							});
						}
					},
				);

				return {
					destroy() {
						unsubscribe?.();
					},
				};
			},
		});
	};
